import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { ApiUsersService } from '@shure/cloud/shared/users/data-access';

/**
 * This Guard ensures we have retrieved and saved accouint information. It's
 * usually critical for the rest of the components to function properly. One
 * key example is knowing the user's correct language. We need to know that
 * up front, or there's a plethora of ag-grid translation problems, but not
 * limited to that.
 */

@Injectable({ providedIn: 'root' })
export class AccountGuard implements CanActivate, CanActivateChild {
	constructor(
		public organizationStoreService: OrganizationsStoreService,
		private apiUsersService: ApiUsersService
	) {}

	public canActivate(): Observable<boolean> {
		return this.doCheck();
	}

	public canActivateChild(): Observable<boolean> {
		return this.doCheck();
	}

	private doCheck(): Observable<boolean> {
		const accountInformationKeys = Object.keys(this.organizationStoreService.accountInformation());
		// If there are account information keys, we already have the account info.
		if (accountInformationKeys.length > 0) {
			return of(true);
		}

		// If there are no account information keys, fetch and save it.
		const accountInfo$ = this.apiUsersService.getSessionAccountInfo$Response().pipe(
			timeout(10_000),
			catchError((error) => of(error))
		);
		return accountInfo$.pipe(
			map((data) => {
				if (data?.body?.body) {
					this.organizationStoreService.setAccountInfo(data?.body?.body);
				}
				return true;
			})
		);
	}
}
